import { useEffect, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MonitorKey } from '@servwell/shared';
import { useTRPC } from '../../../lib/trpc';
import { StatusBadge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { PressedGroup } from '../../ui/PressedGroup';
import {
  StaleNotice,
  UnresolvedState,
  awaitingServer,
  refreshFailed,
  refreshUntilShown,
  shownState,
  unresolved,
} from '../../ui/QueryStates';
import { useTapOnce } from '../../ui/useTapOnce';
import { sendOnce } from '../../tickets/sendOnce';
import { IconSettings } from '../../icons';
import {
  REVIEW_EXPLAINER,
  emptyReviewLine,
  evidenceLines,
  judgedLine,
  reviewStateLine,
  shopTime,
} from '../monitorsView';
import { ModeSwitch } from './ModeSwitch';
import { monitorHeadingId } from './MonitorSettingsRow';
import type { MonitorSettingsRowData, ReviewAlert } from './types';

/**
 * The testing review (spec §7): for each monitor in Testing, everything it raised in the last
 * 7 days (a DEDICATED read, `monitor.review`: open, cleared by a check AND resolved by a
 * person), each with Useful / Not useful (this person's, the latest standing), "N of M judged
 * useful", and Switch to Live. Owner only (`alert:review`), as the card is.
 *
 * Each monitor's list is read when it is opened. A failed read says so; it never reads as
 * "nothing raised", and an empty answer is an all-clear only from a monitor being checked.
 */
export function TestingReview({
  rows,
  now,
}: {
  /** Every monitor on the card; the review draws those in Testing. */
  rows: MonitorSettingsRowData[];
  now: Date;
}) {
  const testing = rows.filter((r) => r.mode === 'testing');
  // The monitor whose review just switched it Live: its review (and the button pressed) is
  // gone, so the focus goes to its row, once that has been drawn as Live.
  const [wentLive, setWentLive] = useState<MonitorKey | null>(null);
  useEffect(() => {
    if (wentLive && !testing.some((r) => r.key === wentLive)) {
      document.getElementById(monitorHeadingId(wentLive))?.focus();
      setWentLive(null);
    }
  }, [wentLive, testing]);

  return (
    <section aria-labelledby="alerts-review" className="mt-5" data-testing-review>
      <h3 id="alerts-review" className="text-sm font-semibold">
        Testing review
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">{REVIEW_EXPLAINER}</p>
      {testing.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground" data-empty="review">
          No monitor is in Testing.
        </p>
      ) : (
        <ul className="mt-2 space-y-2">
          {testing.map((row) => (
            <ReviewBlock
              key={row.key}
              row={row}
              now={now}
              onWentLive={() => setWentLive(row.key)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export function ReviewBlock({
  row,
  now,
  onWentLive,
  initiallyOpen = false,
}: {
  row: MonitorSettingsRowData;
  now: Date;
  onWentLive: () => void;
  /** For a test: the block as it is drawn once opened. */
  initiallyOpen?: boolean;
}) {
  const [open, setOpen] = useState(initiallyOpen);
  const bodyId = useId();
  return (
    <li
      className="min-w-0 rounded-[var(--radius-sm)] border border-border p-3"
      data-review={row.key}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="min-w-0 text-sm font-semibold break-words">
          Testing: {row.label}
        </h4>
        <Button
          variant="ghost"
          size="sm"
          className="min-h-9"
          aria-expanded={open}
          aria-controls={open ? bodyId : undefined}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? 'Hide what it raised' : 'Review what it raised'}
        </Button>
      </div>
      {open ? (
        <div id={bodyId} className="mt-2">
          <ReviewBody row={row} now={now} onWentLive={onWentLive} />
        </div>
      ) : null}
    </li>
  );
}

function ReviewBody({
  row,
  now,
  onWentLive,
}: {
  row: MonitorSettingsRowData;
  now: Date;
  onWentLive: () => void;
}) {
  const trpc = useTRPC();
  const q = useQuery(trpc.monitor.review.queryOptions({ key: row.key }));
  const state = shownState(q, q.data);
  if (!q.data) {
    return (
      <UnresolvedState
        state={unresolved(state)}
        what="what it raised"
        icon={IconSettings}
        error={q.error}
        onRetry={() => void q.refetch()}
        skeleton="h-16"
      />
    );
  }
  const page = q.data;
  return (
    <div className="space-y-2">
      <StaleNotice show={refreshFailed(q, page)} onRetry={() => void q.refetch()} />
      {page.total === 0 ? (
        <p className="text-sm text-muted-foreground" data-empty="raised">
          {emptyReviewLine(row.health)}
        </p>
      ) : (
        <ul className="space-y-2">
          {page.alerts.map((a) => (
            <ReviewItem
              key={a.id}
              alert={a}
              monitorKey={row.key}
              tz={page.timeZone}
              now={now}
            />
          ))}
        </ul>
      )}
      {page.total > page.alerts.length ? (
        <p className="text-xs text-muted-foreground">
          Showing the newest {page.alerts.length.toLocaleString('en-US')} of{' '}
          {page.total.toLocaleString('en-US')}.
        </p>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-2 border-t border-border pt-2">
        <p className="py-1 text-sm font-medium" data-judged>
          {judgedLine(page)}
        </p>
        <ModeSwitch row={row} onLanded={onWentLive} />
      </div>
    </div>
  );
}

type Judgment = 'useful' | 'not_useful' | 'none';

const JUDGMENTS = [
  { value: 'useful', label: 'Useful' },
  { value: 'not_useful', label: 'Not useful' },
] as const;

export function ReviewItem({
  alert,
  monitorKey,
  tz,
  now,
}: {
  alert: ReviewAlert;
  monitorKey: MonitorKey;
  tz: string;
  now: Date;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const lock = useTapOnce();
  const [error, setError] = useState<string | null>(null);
  const judge = useMutation(
    trpc.alert.judge.mutationOptions({
      onSuccess: () => {
        setError(null);
        return refreshUntilShown(qc, {
          queryKey: trpc.monitor.review.queryKey({ key: monitorKey }),
        });
      },
      onError: (e) => setError(e.message),
      onSettled: () => lock.release(),
    }),
  );
  const { headline, details } = evidenceLines(alert.evidence, tz, now, alert.subjectType);
  const current: Judgment =
    alert.useful === null ? 'none' : alert.useful ? 'useful' : 'not_useful';
  const busy = awaitingServer(judge);
  return (
    <li
      className="min-w-0 rounded-[var(--radius-sm)] border border-border p-2"
      data-review-alert={alert.status === 'resolved' ? alert.resolvedKind : 'open'}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="min-w-0 flex-[1_1_12rem] text-sm font-medium break-words">
          {headline}
        </p>
        <StatusBadge tone={alert.status === 'resolved' ? 'neutral' : 'warn'}>
          {reviewStateLine(alert, tz, now)}
        </StatusBadge>
      </div>
      {details.length ? (
        <p className="mt-0.5 text-sm break-words text-muted-foreground">
          {details.join(' · ')}
        </p>
      ) : null}
      {alert.resolvedKind === 'human' && alert.resolutionNote ? (
        <p className="mt-0.5 text-sm break-words">
          What was done: {alert.resolutionNote}
        </p>
      ) : null}
      <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          First found {shopTime(alert.firstFiredAt, tz, now)}
        </p>
        <PressedGroup<Judgment>
          label={`Was it useful? ${headline}`}
          options={JUDGMENTS}
          value={current}
          busy={busy}
          onChange={(next) => {
            if (next === current || next === 'none') return;
            sendOnce(lock, judge.mutate, {
              alertId: alert.id,
              useful: next === 'useful',
            });
          }}
        />
      </div>
      {error ? (
        <p role="alert" className="mt-1 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </li>
  );
}
